<template>
  <section id="realtime-tracker" aria-label="live finish tracker">
    <div class="mobile-top-banner">
      <button class="close-btn" aria-label="Close live tracker" @click="goBack">
        <svg
          data-v-19e2e6f8=""
          aria-hidden="true"
          focusable="false"
          data-prefix="fas"
          data-icon="chevron-circle-left"
          role="img"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 512 512"
          class="svg-inline--fa fa-chevron-circle-left fa-w-16"
        >
          <path
            data-v-19e2e6f8=""
            fill="currentColor"
            d="M256 504C119 504 8 393 8 256S119 8 256 8s248 111 248 248-111 248-248 248zM142.1 273l135.5 135.5c9.4 9.4 24.6 9.4 33.9 0l17-17c9.4-9.4 9.4-24.6 0-33.9L226.9 256l101.6-101.6c9.4-9.4 9.4-24.6 0-33.9l-17-17c-9.4-9.4-24.6-9.4-33.9 0L142.1 239c-9.4 9.4-9.4 24.6 0 34z"
            class=""
          ></path>
        </svg>
      </button>
      <p>{{ YACHT_TRACKER_RACE_NAME }} {{ YACHT_TRACKER_RACE_YEAR }}</p>
    </div>

    <div v-show="!YACHT_TRACKER_IS_LIVE_RACE" id="error-message">
      <p>
        The Live Finish Tracker is currently only available for live races. If you'd like to replay a race, please visit the <a href="/tracker">Full Fleet Yacht Tracker</a>.
      </p>
    </div>

    <section v-show="YACHT_TRACKER_IS_LIVE_RACE" id="live-finish-tracker-info" aria-label="about and controls">
      <h4>About</h4>
      <p>
        This Live Finish Tracker shows the positions of boats in the last part of the course
        based on the most recent reports received from satellite units on the
        boats. Those positions are up to 10 minutes earlier than the positions
        reflected in Standings and Tracker. A boat whose unit is not reporting
        may not show on the Live Finish Tracker.
      </p>
      <h4>Controls</h4>
      <p>
        <strong>Pan:</strong> Pan the map by clicking and dragging the cursor.
        On a touch device, use one finger to drag and pan the map.
      </p>
      <p>
        <strong>Zoom:</strong> Use mouse scroll-wheel, or two-finger pinch
        gesture to zoom in or out. Double-click to zoom in. Hold down
        <code>Shift</code> and drag the cursor to zoom to a selected area.
      </p>
      <p>
        <strong>Rotate/pitch:</strong> Rotate the map by clicking and dragging
        with the right mouse button, or by clicking and dragging with the left
        mouse button while holding down the <code>Ctrl</code> key. On touch
        devices, use two-finger rotate gesture to rotate the map; use two-finger
        up/down dragging to change the pitch.
      </p>
    </section>

    <section v-show="DEBUG_MODE">
      <table>
        <tbody>
          <tr>
            <th>Center</th><td>{{ debug.center }}</td>
          </tr>
          <tr>
            <th>Pitch</th><td>{{ debug.pitch }}</td>
          </tr>
          <tr>
            <th>Zoom</th><td>{{ debug.zoom }}</td>
          </tr>
          <tr>
            <th>Bearing</th><td>{{ debug.bearing }}</td>
          </tr>
        </tbody>
      </table>
    </section>

    <section
      v-show="YACHT_TRACKER_IS_LIVE_RACE"
      id="map-wrapper"
      aria-label="tracker map of yachts in the river derwent"
    >
      <div id="realtime-map"></div>
      <div id="live-finish-badge">Live finish</div>
    </section>

    <section
      v-show="YACHT_TRACKER_IS_LIVE_RACE"
      id="live-finish-tracker-attr"
      aria-label="acknowledgements"
    >
      <p>
        Acknowledgements and thanks:
        <a href="http://www.openmaptiles.org/">OpenMapTiles</a> and
        <a href="http://www.openstreetmap.org/about/"
          >OpenStreetMap contributors</a
        >, <a href="https://maplibre.org/about/">MapLibre and contributors</a>,
        <a href="https://github.com/jscastro76/threebox/">Threebox</a> by
        <a href="https://github.com/jscastro76/">jscastro76</a>,
        <a href="https://github.com/peterqliu">peterqliu</a> and contributors.
        3D Model
        <a
          href="https://sketchfab.com/3d-models/sailing-boat-198940e8671e4c1a8b919bb5c839d555"
          >"Sailing Boat" by 3DDomino</a
        >.
      </p>
    </section>
  </section>
</template>

<style lang="scss">
// cannot use "scoped"
#error-message {
  padding: 10px 20px;
}

#realtime-tracker {
  .mobile-top-banner {
    display: none;
  }

  #map-wrapper {
    position: relative;
    width: 100%;
    height: 650px;
    overflow: hidden;

    #realtime-map {
      height: 100%;
      width: 100%;
    }

    #live-finish-badge {
      position: absolute;
      bottom: 10px;
      left: 10px;
      background-color: rgb(255, 0, 17);
      color: white;
      padding: 10px;
      text-transform: uppercase;
      font-family: Din_Med, Arial;
    }
  }

  /* for 3d generated labels */
  .yacht-label {
    background: #011552b3;
    padding: 2px 5px;
    color: white;
    display: flex;
    font-size: 1em;
    align-items: baseline;
    margin-left: 150px;
    min-width: 150px;

    .status-icon {
      display: inline-block;
      background: url("/assets/icon-fleet.png");
      background-size: cover;
      width: 1em;
      height: 1em;
      margin-right: 10px;

      &.record-holder {
        background: url("/assets/icon-record-holder.png");
        background-size: cover;
      }

      &.division-leader {
        background: url("/assets/icon-division-leader.png");
        background-size: cover;
      }

      &.race-leader {
        background: url("/assets/icon-race-leader.png");
        background-size: cover;
      }

      &.retired {
        background: url("/assets/icon-retired.png");
        background-size: cover;
      }
    }

    .yacht-time {
      font-size: 0.8em;
      font-style: italic;
      margin-left: 10px;
    }
  }
}

@media screen and (max-width: 480px) {
  html,
  body {
    margin: 0px;
  }

  #realtime-tracker {
    --top-banner-height: 50px;

    .mobile-top-banner {
      display: flex;
      background-color: rgba(38, 59, 89);
      color: white;
      opacity: 90;
      text-align: center;
      font-family: Din_Med, Arial;
      text-transform: uppercase;
      height: var(--top-banner-height);
      align-items: baseline;

      p {
        padding: 0;
        margin: 15px 10px;
        font-size: 0.8rem;
      }

      .close-btn {
        height: var(--top-banner-height);
        width: var(--top-banner-height);
        color: white;
        font-size: 1rem;
        font-family: Din_Reg, Arial;
        line-height: 1.25rem;
        left: 0px;
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        background-color: rgba(38, 59, 89);
        border: unset;

        .svg-inline--fa {
          display: inline-block;
          font-size: inherit;
          height: 1em;
          overflow: visible;
          vertical-align: -0.125em;
          width: 1em;
          overflow: visible;
        }
      }
    }

    #live-finish-tracker-info {
      display: none;
    }

    #live-finish-tracker-attr {
      display: none;
    }

    #map-wrapper {
      position: relative;
      height: calc(
        100vh - var(--top-banner-height)
      ) !important; /* still need height on non-mobile */

      #realtime-map {
        height: 100%;
        width: 100%;
      }
    }
  }
}
</style>

<script>
import maplibregl from 'maplibre-gl'
import { Threebox, THREE } from 'threebox-plugin'
import { mapState } from 'vuex'
import { relativeTime } from '@/helpers/racetime'

const YACHT_SCALE = 0.5
const YACHT_LABEL_ALTITUDE = 500 * YACHT_SCALE

export default {
  name: 'App',
  data () {
    return {
      firstRender: true,
      firstRenderInProgress: false,
      map: null,
      tb: null,
      mapContainer: 'realtime-map',
      mapStyle: 'https://maps.cycaracing.com/styles/klokantech-basic/style.json',
      initialCenter: [0, 0],
      initialZoom: 12,
      initialPitch: 60,
      initialBearing: 0,
      antialias: true,
      features: {
        add3DYachts: true,
        add3DLabels: false,
        add3DTracks: true,
        addMapboxLabels: true,
        addDebuggingMarkers: false,
        addRealSunlight: false, /* causes resize issue */
        showDevLogs: false
      },
      debug: {
        center: [0, 0],
        zoom: 12,
        pitch: 60,
        bearing: 0
      },
      yachts: {},
      selectedRaceTime: '',
      yachtLabelUpdateTimeout: null,
      poll: null,
      iPhoneDetected: false
    }
  },
  async mounted () {
    this.features.add3DYachts && await this.$store.dispatch('init')
    this.selectedRaceTime = this.raceTime

    if (!this.YACHT_TRACKER_IS_LIVE_RACE) return

    this.initialCenter = [this.LIVE_TRACKER_CENTER_LON, this.LIVE_TRACKER_CENTER_LAT]
    this.initialZoom = this.LIVE_TRACKER_ZOOM
    this.initialPitch = this.LIVE_TRACKER_PITCH
    this.initialBearing = this.LIVE_TRACKER_BEARING

    this.$nextTick(() => {
      this.initMap(this.fleetData, this.tracks)

      this.yachtLabelUpdateTimeout = setTimeout(function updateYachtTimes () {
        const elements = document.querySelectorAll('#realtime-map .yacht-label .yacht-time')
        elements.forEach(el => {
          el.textContent = relativeTime(el.dataset.time)
        })
        this.yachtLabelUpdateTimeout = setTimeout(updateYachtTimes, 1000)
      }, 1000)

      this.poll = setTimeout(this.pollingRefresh, 10000)
    })
  },
  async destroyed () {
    if (!this.YACHT_TRACKER_IS_LIVE_RACE) {
      this.$store.dispatch('reset')
      return
    }

    if (window.tb) {
      await window.tb.dispose() // also destroys map
    }
    window.yachts = undefined
    window.yachtLabels = undefined
    this.$store.dispatch('reset')
    clearTimeout(this.yachtLabelUpdateTimeout)
    clearTimeout(this.poll)
  },
  computed: {
    ...mapState('race', [
      'raceId',
      'status',
      'fleetData',
      'fleetSource',
      'rhumbline',
      'raceTime',
      'raceDateTime',
      'raceTimeOptions',
      'tracks'
    ]),
    ...mapState('config', [
      'YACHT_TRACKER_RACE_NAME',
      'YACHT_TRACKER_RACE_YEAR',
      'YACHT_TRACKER_IS_LIVE_RACE',
      'LIVE_TRACKER_CENTER_LON',
      'LIVE_TRACKER_CENTER_LAT',
      'LIVE_TRACKER_ZOOM',
      'LIVE_TRACKER_PITCH',
      'LIVE_TRACKER_BEARING',
      'DEBUG_MODE'
    ])
  },
  methods: {
    async pollingRefresh () {
      if (this.firstRender || this.firstRenderInProgress) {
        return
      }

      this.features.add3DYachts && await this.$store.dispatch('race/fetchNewData', { raceTime: '' })

      this.$nextTick(() => {
        this.updateMap(this.fleetData)
      })

      this.poll = setTimeout(this.pollingRefresh, 10000)
    },
    initMap (fleetData) {
      this.firstRender = true
      this.firstRenderInProgress = true

      this.map = new maplibregl.Map({
        container: this.mapContainer,
        style: this.mapStyle,
        zoom: this.initialZoom,
        center: this.initialCenter,
        pitch: this.initialPitch, // max pitch is 60
        bearing: this.initialBearing,
        antialias: this.antialias // create the gl context with MSAA antialiasing, so custom layers are antialiased
      })

      this.map.addControl(new maplibregl.FullscreenControl())
      this.map.addControl(new maplibregl.NavigationControl())
      this.map.addControl(new maplibregl.ScaleControl({ unit: 'nautical' }), 'top-left')

      this.map.on('style.load', async () => {
        /* fill water with dark blue */
        this.map.setPaintProperty('water', 'fill-color', '#254c88')

        this.addRhumbline()

        /* initialise tb */
        window.tb = new Threebox(
          this.map,
          this.map.getCanvas().getContext('webgl'),
          {
            defaultLights: true,
            enableSelectingObjects: true,
            multiLayer: true,
            realSunlight: this.features.addRealSunlight
          }
        )

        /* add 3d assets */
        this.addYachts(fleetData)
        this.addLabels(fleetData)
        this.addTracks(fleetData)

        /* add debugging stuff */
        this.addMapboxLabels(fleetData)
      })

      this.map.on('idle', () => {
        this.firstRender = false
        this.firstRenderInProgress = false
      })

      if (this.DEBUG_MODE) {
        this.map.on('move', () => {
          this.features.showDevLogs && console.log('moved!')
          this.debug.center = this.map.getCenter()
          this.debug.pitch = this.map.getPitch()
          this.debug.zoom = this.map.getZoom()
          this.debug.bearing = this.map.getBearing()
        })
      }
    },
    async updateMap (fleetData) {
      this.features.showDevLogs && console.log('updateMap')
      /* update source and mapbox */
      if (this.map.getSource('fleet')) this.map.getSource('fleet').setData(JSON.parse(JSON.stringify(this.fleetSource.data)))

      const yachtsToDelete = []
      if (this.features.add3DYachts && window.yachts) {
        const yachtsOnMap = Object.keys(window.yachts)
        for (let i = 0; i < yachtsOnMap.length; i++) {
          const yachtInFleet = fleetData.find(y => y.RaceEntryId.toString() === yachtsOnMap[i])
          if (!yachtInFleet) {
            yachtsToDelete.push(yachtsOnMap[i])
          }
        }
      }

      const labelsToDelete = []
      if (this.features.add3DLabels && window.yachtLabels) {
        const labelsOnMap = Object.keys(window.yachtLabels)
        for (let i = 0; i < labelsOnMap.length; i++) {
          const labelInFleet = fleetData.find(y => y.RaceEntryId.toString() === labelsOnMap[i])
          if (!labelInFleet) {
            labelsToDelete.push(labelsOnMap[i])
          }
        }
      }

      const tracksToDelete = []
      if (this.features.add3DTracks && window.tracks) {
        const tracksOnMap = Object.keys(window.tracks)
        for (let i = 0; i < tracksOnMap.length; i++) {
          const trackInFleet = fleetData.find(y => y.RaceEntryId.toString() === tracksOnMap[i])
          if (!trackInFleet) {
            tracksToDelete.push(tracksOnMap[i])
          }
        }
      }

      for (let i = 0; i < yachtsToDelete.length; i++) {
        window.tb.remove(window.yachts[yachtsToDelete[i]])
        delete window.yachts[yachtsToDelete[i]]
      }

      for (let i = 0; i < labelsToDelete.length; i++) {
        window.tb.remove(window.yachtLabels[labelsToDelete[i]])
        delete window.yachtLabels[labelsToDelete[i]]
      }

      for (let i = 0; i < tracksToDelete.length; i++) {
        window.tb.remove(window.tracks[tracksToDelete[i]])
        delete window.tracks[tracksToDelete[i]]
      }

      if (this.features.add3DYachts && window.yachts) {
        for (let i = 0; i < fleetData.length; i++) {
          const yachtData = fleetData[i]

          if (!window.yachts[`${yachtData.RaceEntryId}`]) {
            await this.addYachtToMap(yachtData)
            continue
          }

          if (!yachtData.ShowOnMap) {
            window.tb.remove(window.yachts[`${yachtData.RaceEntryId}`])
            delete window.yachts[`${yachtData.RaceEntryId}`]
            continue
          }

          window.yachts[`${yachtData.RaceEntryId}`].setCoords([yachtData.render.longitude, yachtData.render.latitude])
          window.yachts[`${yachtData.RaceEntryId}`].setRotation({ z: -1 * (yachtData.render.cog || 0.001) }) // setting z rotation to 0 does nothing
        }
      }

      /* update yacht labels */
      if (this.features.add3DLabels && window.yachtLabels) {
        for (let i = 0; i < fleetData.length; i++) {
          const yachtData = fleetData[i]

          // this could be optimised, just remove them as the label can change
          if (window.yachtLabels[`${yachtData.RaceEntryId}`]) {
            window.tb.remove(window.yachtLabels[`${yachtData.RaceEntryId}`])
            delete window.yachtLabels[`${yachtData.RaceEntryId}`]
          }

          if (yachtData.ShowOnMap) {
            this.addYachtLabelToMap(yachtData)
          }
        }
      }

      /* update yacht tracks */
      if (this.features.add3DTracks && window.tracks) {
        for (let i = 0; i < fleetData.length; i++) {
          const trackData = { raceEntryId: fleetData[i].RaceEntryId, track: fleetData[i].History }

          let trackColor = null

          if (window.tracks[`${trackData.raceEntryId}`]) {
            trackColor = window.tracks[`${trackData.raceEntryId}`].material.color
            window.tb.remove(window.tracks[`${trackData.raceEntryId}`])
            delete window.tracks[`${trackData.raceEntryId}`]
          }

          this.addTrackToMap(trackData, trackColor)
        }
      }

      /* repaint */
      window.tb.update()
      this.map.repaint = true

      /* debugging */
      this.features.showDevLogs && console.log('fleetData', fleetData.length)
      this.features.showDevLogs && console.log('window.yachts', window.yachts && Object.keys(window.yachts).length)
      this.features.showDevLogs && console.log('window.yachtLabels', window.yachtLabels && Object.keys(window.yachtLabels).length)
      this.features.showDevLogs && console.log('window.tracks', window.tracks && Object.keys(window.tracks).length)
    },
    addYachts (fleetData) {
      if (this.features.add3DYachts && this.map.getLayer('3d-yachts') == null) {
        window.yachts = {}

        this.map.addLayer({
          id: '3d-yachts',
          type: 'custom',
          renderingMode: '3d',
          onAdd: (map, mbxContext) => {
            for (let i = 0; i < fleetData.length; i++) {
              this.addYachtToMap(fleetData[i])
            }
          },
          render: (gl, matrix) => {
            if (this.features.addRealSunlight && this.raceDateTime) window.tb.setSunlight(this.raceDateTime)
            window.tb.update()
          }
        })
      }
    },
    addLabels (fleetData) {
      if (this.features.add3DLabels && this.map.getLayer('3d-labels') == null) {
        window.yachtLabels = {}

        this.map.addLayer({
          id: '3d-labels',
          type: 'custom',
          renderingMode: '3d',
          onAdd: (map, mbxContext) => {
            for (let i = 0; i < fleetData.length; i++) {
              this.addYachtLabelToMap(fleetData[i])
            }
          },
          render: (gl, matrix) => {
            window.tb.update()
          }
        })
      }
    },
    addTracks (fleetData) {
      if (this.features.add3DTracks && this.map.getLayer('3d-tracks') == null) {
        window.tracks = {}

        this.map.addLayer({
          id: '3d-tracks',
          type: 'custom',
          renderingMode: '3d',
          onAdd: (map, mbxContext) => {
            for (let i = 0; i < fleetData.length; i++) {
              this.addTrackToMap({ raceEntryId: fleetData[i].RaceEntryId, track: fleetData[i].History })
            }
          },
          render: (gl, matrix) => {
            window.tb.update()
          }
        })
      }
    },
    addTrackToMap (trackData /* {raceEntryId: number; track: Array<YachtTrackPoint>; } */, trackColor = null) {
      let color
      if (trackColor === null) {
        color = new THREE.Color(0xffffff)
        color.setHex(Math.random() * 0xffffff)
      } else {
        color = trackColor
      }

      if (trackData.track.length > 0) {
        const trackCoords = trackData.track.map(tp => [tp.Longitude, tp.Latitude])
        const trackline = window.tb.line({
          geometry: trackCoords,
          width: 2,
          color
        })
        window.tb.add(trackline)
        window.tracks[`${trackData.raceEntryId}`] = trackline
      }
    },
    addYachtToMap (yachtData) {
      this.features.showDevLogs && console.log('addYachtToMap', yachtData.RaceEntryId)
      var options = {
        obj: '/assets/3d/boat.gltf',
        type: 'gltf',
        scale: YACHT_SCALE,
        units: 'meters',
        rotation: { x: 90, y: 180, z: 0 }, // default rotation (facing north)
        anchor: 'center',
        bbox: false
      }

      window.tb.loadObj(options, (model) => {
        const yacht = model.setCoords([yachtData.render.longitude, yachtData.render.latitude])
        yacht.setRotation({ z: -1 * yachtData.render.cog || 0.001 }) // setting z rotation to 0 does nothing.

        // eslint-disable-next-line
        yacht.addEventListener('SelectedChange', (e) => this.onSelectedChange(e, yachtData.RaceEntryId), false)

        yacht.castShadow = this.features.addRealSunlight

        window.tb.add(yacht)
        window.yachts[`${yachtData.RaceEntryId}`] = yacht
      })
    },
    addYachtLabelToMap (yachtData) {
      const labelDiv = document.createElement('div')
      labelDiv.className = 'yacht-label'
      const iconSpan = document.createElement('span')
      iconSpan.className = 'status-icon ' + yachtData.status
      const labelSpan = document.createElement('span')
      labelSpan.textContent = `${yachtData.Name} / ${yachtData.render.sog}kt`
      const timeSpan = document.createElement('span')
      timeSpan.className = 'yacht-time'
      const timeAttr = document.createAttribute('data-time')
      timeAttr.value = yachtData.RaceTime
      timeSpan.setAttributeNode(timeAttr)

      labelDiv.appendChild(iconSpan)
      labelDiv.appendChild(labelSpan)
      labelDiv.appendChild(timeSpan)

      const label = window.tb.label({ htmlElement: labelDiv, alwaysVisible: true })
      label.setCoords([yachtData.render.longitude, yachtData.render.latitude, YACHT_LABEL_ALTITUDE])
      window.tb.add(label)
      window.yachtLabels[`${yachtData.RaceEntryId}`] = label
    },
    async addMapboxLabels () {
      if (!this.map.getSource('fleet')) {
        /* add source */
        this.map.addSource('fleet', {
          type: 'geojson',
          data: JSON.parse(JSON.stringify(this.fleetSource.data))
        })
      }

      const icons = [{
        path: '/assets/icon-division-leader.png',
        iconName: 'icon-division-leader'
      }, {
        path: '/assets/icon-fleet.png',
        iconName: 'icon-fleet'
      }, {
        path: '/assets/icon-race-leader.png',
        iconName: 'icon-race-leader'
      }, {
        path: '/assets/icon-record-holder.png',
        iconName: 'icon-record-holder'
      }, {
        path: '/assets/icon-retired.png',
        iconName: 'icon-retired'
      }]

      if (this.features.addMapboxLabels && this.map.getLayer('yacht-names') == null) {
        Promise.all(
          icons.map(icon => new Promise((resolve, reject) => {
            this.map.loadImage(icon.path, (error, image) => {
              if (error) reject(error)
              this.map.addImage(icon.iconName, image)
              resolve()
            })
          }))
        )
          .then(() => {
            this.map.addLayer({
              id: 'yacht-names',
              type: 'symbol',
              source: 'fleet',
              layout: {
                'text-font': ['Klokantech Noto Sans Regular'],
                'text-field': [
                  'format',
                  ['upcase', ['get', 'title']],
                  { 'font-scale': 1 },
                  '\n',
                  {},
                  ['downcase', ['get', 'sog']],
                  { 'font-scale': 0.8 },
                  ' ',
                  {},
                  ['downcase', ['get', 'relativeTime']],
                  { 'font-scale': 0.6 }
                ],
                'text-justify': 'left',
                'text-offset': [1.5, 0],
                'text-anchor': 'top-left',
                'icon-image': ['get', 'icon'],
                'icon-size': 0.2,
                'icon-anchor': 'top-left',
                'icon-offset': [0, 0],
                'icon-allow-overlap': true, // allows yacht labels to overlap
                'text-allow-overlap': true
              },
              paint: {
                'text-color': '#fff',
                'text-halo-color': '#263b59',
                'text-halo-width': 2
              }
            })
          })
      }
    },
    addDebuggingMarkers (fleetData) {
      /* markers for debugging */
      if (this.features.addDebuggingMarkers) {
        new maplibregl.Marker()
          .setLngLat(this.center)
          .addTo(this.map)

        for (let i = 0; i < fleetData.length; i++) {
          const yacht = fleetData[i]
          new maplibregl.Marker()
            .setLngLat([yacht.render.longitude, yacht.render.latitude])
            .addTo(this.map)
        }
      }
    },
    addRhumbline () {
      this.map.addSource('rhumbline', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: this.rhumbline.map(rl => [rl.Longitude, rl.Latitude])
          }
        },
        cluster: false
      })

      this.map.addLayer({
        id: 'rhumbline',
        type: 'line',
        source: 'rhumbline',
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#fff',
          'line-width': 1,
          'line-dasharray': [2, 2]
        }
      })
    },
    onSelectedChange (e, raceEntryId) {
      const yachtData = this.fleetData.find(c => c.RaceEntryId === raceEntryId)
      this.features.showDevLogs && console.log('selected', yachtData)
    },
    incrementSelectedRacetime () {
      if (this.firstRender || this.firstRenderInProgress) return
      const curIndex = this.raceTimeOptions.findIndex(rt => rt === this.selectedRaceTime)
      if (this.curIndex !== -1 && curIndex < this.raceTimeOptions.length - 1) {
        this.selectedRaceTime = this.raceTimeOptions[curIndex + 1]
      }
    },
    decrementSelectedRacetime () {
      if (this.firstRender || this.firstRenderInProgress) return
      const curIndex = this.raceTimeOptions.findIndex(rt => rt === this.selectedRaceTime)
      if (this.curIndex !== -1 && curIndex > 0) {
        this.selectedRaceTime = this.raceTimeOptions[curIndex - 1]
      }
    },
    toggleLayer (layerName) {
      this.features.showDevLogs && console.log(this.map.setLayoutProperty(layerName, 'visibility', 'none'))
    },
    goBack () {
      window.history.back()
    }
  }
}
</script>
